// Aufklappfläche für Menüs und Auswahlen: per Portal an <body>, feste Position am Anker, klappt nach oben,
// wenn unten kein Platz ist, bleibt immer im Fenster (nie abgeschnitten). Schließt bei Klick daneben und Escape.
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { ebene } from "../werkzeuge/tasten";

interface Props {
  anker: HTMLElement | null;
  offen: boolean;
  onSchliessen: () => void;
  children: ReactNode;
  /** feste Breite in px, „anker“ = so breit wie der Anker, sonst Inhalt (min. minBreite) */
  breite?: number | "anker";
  minBreite?: number;
  ausrichtung?: "start" | "end";
  maxHoehe?: number;
  className?: string;
  /** Rolle für Screenreader */
  rolle?: "menu" | "listbox" | "dialog";
}

const RAND = 8;
const ABSTAND = 4;
// Nur die oberste offene Fläche reagiert auf Escape.
const stapel: number[] = [];
let naechsteId = 1;

interface Lage {
  top?: number;
  bottom?: number;
  left: number;
  maxHeight: number;
  width?: number;
  minWidth?: number;
  oben: boolean;
}

export function Ausklapp({ anker, offen, onSchliessen, children, breite, minBreite = 180, ausrichtung = "start", maxHoehe = 420, className, rolle = "dialog" }: Props) {
  const panel = useRef<HTMLDivElement>(null);
  const [lage, setLage] = useState<Lage | null>(null);
  const schliessenRef = useRef(onSchliessen);
  schliessenRef.current = onSchliessen;

  useLayoutEffect(() => {
    if (!offen || !anker) {
      setLage(null);
      return;
    }
    const berechne = () => {
      const p = panel.current;
      if (!p || !anker.isConnected) return;
      const r = anker.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const width = breite === "anker" ? Math.max(r.width, minBreite) : typeof breite === "number" ? breite : undefined;
      const natuerlich = p.scrollHeight;
      const platzUnten = vh - r.bottom - RAND - ABSTAND;
      const platzOben = r.top - RAND - ABSTAND;
      const oben = natuerlich > platzUnten && platzOben > platzUnten;
      const maxHeight = Math.max(80, Math.min(maxHoehe, oben ? platzOben : platzUnten));
      const w = Math.min(width ?? p.offsetWidth, vw - 2 * RAND);
      let left = ausrichtung === "end" ? r.right - w : r.left;
      left = Math.max(RAND, Math.min(left, vw - w - RAND));
      setLage(
        oben
          ? { bottom: vh - r.top + ABSTAND, left, maxHeight, width: width !== undefined ? w : undefined, minWidth: minBreite, oben }
          : { top: r.bottom + ABSTAND, left, maxHeight, width: width !== undefined ? w : undefined, minWidth: minBreite, oben },
      );
    };
    berechne();
    const ro = new ResizeObserver(berechne);
    if (panel.current) ro.observe(panel.current);
    window.addEventListener("resize", berechne);
    window.addEventListener("scroll", berechne, true);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", berechne);
      window.removeEventListener("scroll", berechne, true);
    };
  }, [offen, anker, breite, minBreite, ausrichtung, maxHoehe]);

  useEffect(() => {
    if (!offen) return;
    const id = naechsteId++;
    stapel.push(id);
    ebene.auf();
    const runter = (e: MouseEvent) => {
      const ziel = e.target as Node;
      if (panel.current?.contains(ziel) || anker?.contains(ziel)) return;
      if (stapel[stapel.length - 1] !== id) return;
      schliessenRef.current();
    };
    const taste = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || stapel[stapel.length - 1] !== id) return;
      e.stopPropagation();
      e.preventDefault();
      schliessenRef.current();
      anker?.focus?.();
    };
    document.addEventListener("mousedown", runter, true);
    document.addEventListener("keydown", taste, true);
    return () => {
      const i = stapel.indexOf(id);
      if (i >= 0) stapel.splice(i, 1);
      ebene.zu();
      document.removeEventListener("mousedown", runter, true);
      document.removeEventListener("keydown", taste, true);
    };
  }, [offen, anker]);

  if (!offen) return null;
  const stil: CSSProperties = {
    position: "fixed",
    top: lage?.top,
    bottom: lage?.bottom,
    left: lage?.left ?? -10000,
    width: lage?.width,
    minWidth: lage?.minWidth ?? minBreite,
    maxHeight: lage?.maxHeight ?? maxHoehe,
    // Während der Messung unsichtbar, aber fokussierbar: mit visibility:hidden greift focus() des Menüs nicht
    // (Pfeiltasten und Enter liefen dann ins Leere), weil die Position erst im Layout-Effekt feststeht.
    opacity: lage ? 1 : 0,
    pointerEvents: lage ? "auto" : "none",
  };
  return createPortal(
    <div
      ref={panel}
      className={`ausklapp${lage?.oben ? " ausklapp--oben" : ""}${className ? ` ${className}` : ""}`}
      style={stil}
      role={rolle}
      data-ausklapp=""
      // React reicht Ereignisse aus Portalen an die Eltern-Komponenten weiter (z. B. Tabellenzeile): hier stoppen.
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {children}
    </div>,
    document.body,
  );
}
